import { useAuth } from '../contexts/AuthContext';
import { User } from '../models/User';
import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { logger } from '../utils/logger';

export const useUser = () => {
  const { currentUser } = useAuth();
  const [user, setUser] = useState<User | null>(currentUser ?? null);
  const [isLoading, setIsLoading] = useState(!currentUser);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!currentUser) {
        setUser(null);
        setIsLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('users')
          .select('*')
          .eq('id', currentUser.uid)
          .maybeSingle();

        if (error) {
          throw error;
        }

        if (data) {
          setUser({
            uid: data.id,
            email: data.email,
            firstName: data.first_name || '',
            lastName: data.last_name || '',
            phoneNumber: data.phone_number || '',
            address: data.address || currentUser.address,
            role: data.role || currentUser.role,
            createdAt: data.created_at || currentUser.createdAt,
            updatedAt: data.updated_at || currentUser.updatedAt,
            orgId: data.org_id,
          });
        } else {
          setUser(currentUser);
        }
      } catch (error) {
        logger.error('Error fetching user data:', error);
        setUser(currentUser);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, [currentUser]);

  const isAdmin = user?.role === 'admin';
  const isDispatch = user?.role === 'dispatch';
  const isCFI = user?.role === 'cfi';
  const hasOrganization = !!user?.orgId;

  return {
    user,
    isAdmin,
    isDispatch,
    isCFI,
    hasOrganization,
    isLoading
  };
};

// Hook to fetch user data by ID
export const useUserById = (userId: string | undefined) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!userId) {
        setUser(null);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from('users')
          .select('*')
          .eq('id', userId)
          .maybeSingle();

        if (error) {
          throw error;
        }

        if (data) {
          setUser({
            uid: data.id,
            email: data.email,
            firstName: data.first_name || '',
            lastName: data.last_name || '',
            phoneNumber: data.phone_number || '',
            address: data.address,
            role: data.role || 'member',
            createdAt: data.created_at || new Date().toISOString(),
            updatedAt: data.updated_at || new Date().toISOString(),
            orgId: data.org_id,
          });
        } else {
          setUser(null);
        }
      } catch (error) {
        logger.error('Error fetching user data by ID:', error);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, [userId]);

  return {
    user,
    isLoading
  };
}; 